import { resolveDashboardAction } from '@/modules/admin-v2/utils/resolveDashboardAction';
import type { DashboardAlert } from '@yawi/types';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface AlertListProps {
  items: DashboardAlert[];
  loading?: boolean;
}

const COLOR_MAP: Record<DashboardAlert['severity'], { bg: string; dot: string; title: string }> = {
  critical: { bg: '#FCEBEB', dot: '#D64545', title: '#7A2020' },
  warning: { bg: '#FAEEDA', dot: '#EF9F27', title: '#854F0B' },
  info: { bg: '#E6F1FB', dot: '#185FA5', title: '#134A80' },
};

export function AlertList({ items, loading }: AlertListProps) {
  const navigate = useNavigate();

  return (
    <div className="bg-white dark:bg-surface-secondary rounded-xl border-[0.5px] border-[#E8E6F8] dark:border-theme-light p-4 h-full flex flex-col">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-[15px] font-semibold text-[#1A1A18] dark:text-theme">
          Alertas
          {!loading && items.length > 0 && (
            <span className="ml-1.5 text-[11px] font-medium text-white bg-[#D64545] rounded-full px-1.5 py-0.5 align-middle">
              {items.length}
            </span>
          )}
        </h4>
        <button
          onClick={() => navigate('/notificaciones')}
          className="text-[12px] text-[#7F77DD] dark:text-primary-400 font-medium hover:underline"
        >
          Ver todas
        </button>
      </div>

      <div className="flex-1 flex flex-col gap-2.5">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-14 bg-[#F0EFF9] dark:bg-theme rounded-lg animate-pulse" />
          ))
        ) : items.length === 0 ? (
          <div className="flex-1 flex items-center justify-center py-6">
            <p className="text-[13px] text-[#888780] dark:text-theme-tertiary">Sin alertas activas</p>
          </div>
        ) : (
          items.map((alert) => {
            const style = COLOR_MAP[alert.severity];
            return (
              <button
                key={alert.id}
                onClick={() => navigate(resolveDashboardAction(alert.ctaAction))}
                className="flex items-start gap-2.5 rounded-lg p-3 text-left transition-opacity hover:opacity-90"
                style={{ backgroundColor: style.bg }}
              >
                <span className="w-2 h-2 rounded-full mt-1.5 flex-shrink-0" style={{ backgroundColor: style.dot }} />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-semibold leading-tight" style={{ color: style.title }}>
                    {alert.title}
                  </p>
                  <p className="text-[11px] text-[#6E6C66] leading-tight mt-0.5">{alert.detail}</p>
                </div>
                {alert.ctaLabel && (
                  <span className="flex items-center gap-0.5 text-[11px] font-medium flex-shrink-0 self-center" style={{ color: style.title }}>
                    {alert.ctaLabel} <ChevronRight size={12} />
                  </span>
                )}
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
